/**
 * Orders Tab Component
 * Orders pending physician co-sign for RN Command Center
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileSignature, ThumbsUp, ThumbsDown, Edit, CheckCircle, Loader2
} from "lucide-react";

const OrdersTab = ({
  isDark,
  ordersStats,
  pendingOrders,
  setSelectedOrder,
  setCosignAction,
  setCosignDialog
}) => {
  return (
    <>
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <Card className={isDark ? "bg-red-900/20 border-red-600/30" : "bg-red-50 border-red-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-500">{ordersStats?.stat || 0}</p>
            <p className={`text-sm ${isDark ? "text-red-300" : "text-red-600"}`}>STAT</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-amber-900/20 border-amber-600/30" : "bg-amber-50 border-amber-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-500">{ordersStats?.urgent || 0}</p>
            <p className={`text-sm ${isDark ? "text-amber-300" : "text-amber-600"}`}>Urgent</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-blue-900/20 border-blue-600/30" : "bg-blue-50 border-blue-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-500">{ordersStats?.routine || 0}</p>
            <p className={`text-sm ${isDark ? "text-blue-300" : "text-blue-600"}`}>Routine</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-purple-900/20 border-purple-600/30" : "bg-purple-50 border-purple-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-purple-500">{ordersStats?.controlled_substances || 0}</p>
            <p className={`text-sm ${isDark ? "text-purple-300" : "text-purple-600"}`}>Controlled</p>
          </CardContent>
        </Card>
      </div>
      
      <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSignature className={isDark ? "text-teal-400" : "text-teal-600"} />
            Orders Pending Physician Co-Sign
          </CardTitle>
          <CardDescription>RN-initiated orders requiring MD review and signature</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            {pendingOrders.length > 0 ? (
              <div className="space-y-3">
                {pendingOrders.map((order) => (
                  <div
                    key={order.id}
                    className={`p-4 rounded-lg border ${
                      order.urgency === 'stat' ? 'border-red-500 bg-red-500/5' :
                      order.urgency === 'urgent' ? 'border-amber-500 bg-amber-500/5' :
                      isDark ? 'border-slate-600' : 'border-gray-200'
                    }`}
                    data-testid={`order-item-${order.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={
                            order.urgency === 'stat' ? 'bg-red-500' :
                            order.urgency === 'urgent' ? 'bg-amber-500' : 'bg-blue-500'
                          }>
                            {order.urgency?.toUpperCase()}
                          </Badge>
                          <Badge variant="outline">{order.order_type}</Badge>
                          {order.controlled_substance_flag && (
                            <Badge className="bg-purple-500">Controlled</Badge>
                          )}
                        </div>
                        <h4 className="font-semibold">{order.patient_name || 'Unknown Patient'}</h4>
                        <p className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                          {order.order_text}
                        </p>
                        {order.clinical_justification && (
                          <p className={`text-xs mt-2 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
                            <strong>Justification:</strong> {order.clinical_justification}
                          </p>
                        )}
                        <p className={`text-xs mt-1 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                          Initiated: {new Date(order.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            setSelectedOrder(order);
                            setCosignAction("approve");
                            setCosignDialog(true);
                          }}
                          data-testid={`approve-order-${order.id}`}
                        >
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedOrder(order);
                            setCosignAction("modify");
                            setCosignDialog(true);
                          }}
                          data-testid={`modify-order-${order.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Modify
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedOrder(order);
                            setCosignAction("reject");
                            setCosignDialog(true);
                          }}
                          data-testid={`reject-order-${order.id}`}
                        >
                          <ThumbsDown className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-12 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <p className="font-medium">All orders have been reviewed</p>
                <p className="text-sm mt-1">No pending orders require co-signature</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </>
  );
};

export default OrdersTab;
